import React, { useRef, useEffect, useState, useMemo } from "react";
import { useReactToPrint } from "react-to-print";

const getPrintTime = () => {
    const now = new Date();

    const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    };

    return now.toLocaleString("en-US", options);
};

const ReceiptTemplate = React.forwardRef(({ order, items, heading }, ref) => {

    // const { CompanyName } = useSelector((state) => state.authReducer);

    const organizeOrderItems = (items) => {
        // console.log("ReceiptTemplate - organizeOrderItems input:", items);

        const parentItems = items;

        const result = [];

        for (const parent of parentItems) {

            const children = parent.dealSelections || [];

            result.push({
                parent,
                children,
            });
        }

        // console.log("ReceiptTemplate - organizeOrderItems result:", result);
        return result;
    };

    const organizedItems = organizeOrderItems(items || []);
    const isLessKot = heading && heading.toUpperCase().includes("LESS");

    // Generate item rows with parent-child structure
    const itemBody = organizedItems?.map((group, groupIdx) => {
        const rows = [];

        // Get ItemLessReason if it exists
        const itemLessReason = group.parent.ItemLessReason ||
            group.parent.ItemLessReasonName ||
            null;

        // Get SpecialInstructions and Comment if they exist
        const specialInstructions = group.parent.SpecialInstructions || group.parent.SpecialInstruction || null;
        const comment = group.parent.Comment || group.parent.Comments || null;

        // Parent item row
        rows.push(
            <div
                key={`parent-${groupIdx}`}
                className="flex justify-between text-sm text-black px-1 py-0.5"
            >
                <span className="w-[20%] font-semibold">{group.parent.quantity}</span>
                <span className="w-[70%] font-medium text-left">
                    {group.parent.name || group.parent.ProductName}
                    {group.parent.SizeName ? ` (${group.parent.SizeName})` : ""}
                    {group.parent.FlavourName ? ` ${group.parent.FlavourName}` : ""}
                </span>
            </div>
        );

        // Show Special Instructions
        if (specialInstructions) {
            rows.push(
                <div
                    key={`parent-inst-${groupIdx}`}
                    className="flex justify-between text-sm text-black px-1 py-0.5"
                >
                    <span className="w-[10%] font-semibold"></span>
                    <strong className="w-[85%] font-medium italic text-left">
                        Item Comment : {specialInstructions}
                    </strong>
                </div>
            );
        }

        // Show Comment
        if (comment) {
            rows.push(
                <div
                    key={`parent-comment-${groupIdx}`}
                    className="flex justify-between text-sm text-black px-1 py-0.5"
                >
                    <span className="w-[10%] font-semibold"></span>
                    <strong className="w-[85%] font-medium italic text-left">
                        Comment : {comment}
                    </strong>
                </div>
            );
        }

        // Show ItemLessReason for LESS items
        if (isLessKot && itemLessReason) {
            rows.push(
                <div
                    key={`parent-reason-${groupIdx}`}
                    className="flex justify-between text-sm text-black px-1 py-0.5"
                >
                    <span className="w-[10%] font-semibold"></span>
                    <strong className="w-[85%] font-medium italic text-left">
                        Reason : {itemLessReason}
                    </strong>
                </div>
            );
        }

        // Child item rows
        if (Object.keys(group?.children)?.length > 0) {
            Object.values(group?.children).forEach((child, childIdx) => {
                if (Array.isArray(child)) {
                    child.forEach((childItem, itemIdx) => {
                        const childReason = childItem.ItemLessReason || childItem.ItemLessReasonName || null;
                        const childSpecialInstructions = childItem.SpecialInstructions || childItem.SpecialInstruction || null;
                        const childComment = childItem.Comment || childItem.Comments || null;

                        rows.push(
                            <div
                                key={`child-${groupIdx}-${childIdx}-${itemIdx}`}
                                className="flex justify-between text-sm text-black px-1 py-0.5"
                            >
                                <span className="w-[1%]"></span>
                                <span className="w-[80%] pl-4 font-normal text-left">
                                    + {childItem.Quantity || 1} {childItem.name}
                                    {childItem.SizeName ? ` (${childItem.SizeName})` : ""}
                                    {childItem.FlavourName ? ` ${childItem.FlavourName}` : ""}
                                </span>
                            </div>
                        );

                        // Show Special Instructions for child items
                        if (childSpecialInstructions) {
                            rows.push(
                                <div
                                    key={`child-inst-${groupIdx}-${childIdx}-${itemIdx}`}
                                    className="flex justify-between text-sm text-black px-1 py-0.5"
                                >
                                    <span className="w-[10%] font-semibold"></span>
                                    <strong className="w-[85%] font-medium italic text-left pl-4">
                                        Item Comment : {childSpecialInstructions}
                                    </strong>
                                </div>
                            );
                        }

                        // Show Comment for child items
                        if (childComment) {
                            rows.push(
                                <div
                                    key={`child-comment-${groupIdx}-${childIdx}-${itemIdx}`}
                                    className="flex justify-between text-sm text-black px-1 py-0.5"
                                >
                                    <span className="w-[10%] font-semibold"></span>
                                    <strong className="w-[85%] font-medium italic text-left pl-4">
                                        Comment : {childComment}
                                    </strong>
                                </div>
                            );
                        }

                        // Show ItemLessReason for LESS items if child has its own reason
                        if (isLessKot && childReason) {
                            rows.push(
                                <div
                                    key={`child-reason-${groupIdx}-${childIdx}-${itemIdx}`}
                                    className="flex justify-between text-sm text-black px-1 py-0.5"
                                >
                                    <span className="w-[10%] font-semibold"></span>
                                    <strong className="w-[85%] font-medium italic text-left pl-4">
                                        Reason : {childReason}
                                    </strong>
                                </div>
                            );
                        }
                    });
                }
            });
        }

        return rows;
    }).flat();


    return (
        <div
            ref={ref}
            style={{
                fontFamily: "Calibri",
                fontSize: "12px",
                margin: "30px",
                display: "none" // Hide from screen
            }}
            className="salesReceipt print-only"
        >
            <style>
                {`
          @media print {
            .print-only {
              display: block !important;
            }
            .no-print {
              display: none !important;
            }
          }
          @media screen {
            .print-only {
              display: none !important;
            }
          }
        `}
            </style>

            <div style={{ width: "100%", textAlign: "center" }}></div>




            {/* <table style={{ width: "100%" }}>
                <thead>
                    <tr>
                        <th style={{
                            textAlign: "center",
                            marginBottom: 0,
                            fontSize: "24px",
                            fontFamily: "Calibri",
                            fontWeight: 600,
                            color: "#000",
                            height: "24px"
                        }}>
                            {CompanyName}
                        </th>
                    </tr>
                </thead>
                <tbody>
                     <tr>
                        <th style={{
                            textAlign: "center",
                            fontSize: "11px",
                            fontWeight: 400,
                            border: 0,
                            color: "#000"
                        }}>
                            {order?.BranchName || "Branch Address"}
                        </th>
                        <th style={{
                            textAlign: "center",
                            fontSize: "11px",
                            fontWeight: 400,
                            border: 0,
                            color: "#000"
                        }}>
                            {order?.AreaName || "Branch Address"}
                        </th>
                    </tr> 
                </tbody>
            </table> */}









            <div className="my-1 border-t-2 border-b-2 border-black text-center w-full">
                {/* Heading */}
                <div className="w-full text-center  font-semibold text-black text-sm border-b-2 border-black">
                    {heading || "KOT"}
                </div>

                {/* Order # */}
                <div className="flex justify-between text-sm text-black font-medium px-1">
                    <strong>Order #</strong>
                    <span>{order?.orderNumber || order?.orderNubmer || order?.OrderNumber || "N/A"}</span>
                </div>

                {/* Date/Time */}
                {(order?.OrderDateTime || order?.orderDateTime) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Date/Time</strong>
                        <span>{order?.orderDateTime || order?.OrderDateTime}</span>
                    </div>
                )}

                {/* Order Mode Row */}
                <div className="flex justify-between text-sm text-black font-medium px-1">
                    <strong>Order Mode</strong>
                    <span>
                        {order?.orderModeName || order?.OrderModeName || order?.OrderMode || "N/A"}
                        {order?.orderSourceName || order?.OrderSourceName ? ` / ${order?.orderSourceName || order?.OrderSourceName}` : ""}
                    </span>
                </div>

                {/* Table */}
                {(order?.TableName || order?.tableName) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Table</strong>
                        <span>{order?.TableName || order?.tableName}</span>
                    </div>
                )}

                {/* Waiter */}
                {(order?.WaiterName || order?.waiterName) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Waiter</strong>
                        <span>{order?.WaiterName || order?.waiterName}</span>
                    </div>
                )}

                {/* Rider */}
                {(order?.RiderName || order?.riderName) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Rider</strong>
                        <span>{order?.RiderName || order?.riderName}</span>
                    </div>
                )}

                {/* Customer Details */}
                {(order?.customerName || order?.CustomerName) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Customer</strong>
                        <span>{order?.customerName || order?.CustomerName}</span>
                    </div>
                )}

                {/* Address - Only for Delivery orders (OrderModeId == 111) */}
                {(order?.orderModeId == 111 || order?.OrderModeId == 111) && (order?.address || order?.Address || order?.CompleteAddress) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Address</strong>
                        <span>{order?.address || order?.Address || order?.CompleteAddress}</span>
                    </div>
                )}

                {/* Phone Number */}
                {(order?.phoneNumber || order?.PhoneNumber) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Phone Number</strong>
                        <span>{order?.phoneNumber || order?.PhoneNumber}</span>
                    </div>
                )}

                {/* Alternate Number */}
                {(order?.AlternateNumber || order?.alternateNumber) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Alternate Number</strong>
                        <span>{order?.alternateNumber || order?.AlternateNumber}</span>
                    </div>
                )}

                {/* Land Mark */}
                {(order?.LandMark || order?.landMark) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Land Mark</strong>
                        <span>{order?.landMark || order?.LandMark}</span>
                    </div>
                )}

                {/* Special Instruction (Order Level) */}
                {(order?.Remarks1 || order?.remarks1) && (
                    <div className="flex justify-between text-sm text-black font-medium px-1">
                        <strong>Special Instruction</strong>
                        <span>{order?.Remarks1 || order?.remarks1}</span>
                    </div>
                )}
            </div>

            <div className="w-full text-sm text-black px-1 ">
                <div className="flex justify-between font-medium text-black text-sm">
                    <strong className="w-[20%]">Qty</strong>
                    <strong className="w-[60%] text-left">Item</strong>
                    <strong className="w-[20%] text-right"></strong>
                </div>
            </div>

            <div className="w-full border-y-2 border-black my-1 px-1 py-1">
                <div>{itemBody}</div>
            </div>






            <br />







            <div className="flex flex-col w-full items-center text-black">
                <span className="italic">Powered by EatX</span>
                <span> Printed on: {getPrintTime()}</span>
            </div>





        </div>
    );
});

const StandardKotPrint = ({
    orderDetails,
    orderAddItems = [],
    orderLessItems = [],
    addHeading = "KOT - ADD",
    lessHeading = "KOT - LESS",
    onComplete = () => { }
}) => {

    const componentRef_Add = useRef();
    const componentRef_Less = useRef();
    const [currentPrintStep, setCurrentPrintStep] = useState(0);

    const handlePrintAdd = useReactToPrint({
        content: () => componentRef_Add.current,
        documentTitle: `Kot_ADD_${orderDetails?.orderNumber || orderDetails?.OrderNumber || "unknown"}`,
        pageStyle: "@page { size: auto; margin: 0mm; }",
        onAfterPrint: () => {
            setCurrentPrintStep(2);
        }
    });

    const handlePrintLess = useReactToPrint({
        content: () => componentRef_Less.current,
        documentTitle: `Kot_LESS_${orderDetails?.orderNumber || orderDetails?.OrderNumber || "unknown"}`,
        pageStyle: "@page { size: auto; margin: 0mm; }",
        onAfterPrint: () => {
            setCurrentPrintStep(3);
        }
    });

    useEffect(() => {
        if (!orderDetails) return;

        if (orderAddItems && orderAddItems.length > 0) {
            setCurrentPrintStep(1);
        } else if (orderLessItems && orderLessItems.length > 0) {
            setCurrentPrintStep(2);
        }
    }, [orderDetails, orderAddItems, orderLessItems]);

    useEffect(() => {
        if (currentPrintStep === 1) {
            handlePrintAdd();
        } else if (currentPrintStep === 2) {
            if (orderDetails && orderLessItems && orderLessItems.length > 0) {
                handlePrintLess();
            } else {
                setCurrentPrintStep(3);
            }
        }
    }, [currentPrintStep]);

    useEffect(() => {
        if (currentPrintStep === 3 && typeof onComplete === "function") {
            onComplete();
        }
    }, [currentPrintStep, onComplete]);

    return (
        <div>
            <ReceiptTemplate
                ref={componentRef_Add}
                order={orderDetails}
                items={orderAddItems}
                heading={addHeading}
            />
            <ReceiptTemplate
                ref={componentRef_Less}
                order={orderDetails}
                items={orderLessItems}
                heading={lessHeading}
            />
        </div>
    );
};

const DepartmentKotPrint = ({
    orderDetails,
    orderAddItems = [],
    orderLessItems = [],
    onComplete = () => { },
    isDuplicate = false,
    departmentAddHeadingBase,
    departmentLessHeadingBase,
}) => {



    const jobRefs = useRef([]);
    const hasStartedRef = useRef(false);
    const hasCompletedRef = useRef(false);
    const [currentJobIndex, setCurrentJobIndex] = useState(null);

    const getHeadingBase = (type) => {
        if (type === "LESS") {
            return (
                departmentLessHeadingBase ||
                (isDuplicate ? "DUPLICATE KOT LESS" : "KOT LESS")
            );
        }

        return (
            departmentAddHeadingBase ||
            (isDuplicate ? "DUPLICATE KOT" : "KOT")
        );
    };

    const groupItemsByDepartment = (items = [], type = "ADD") => {
        if (!Array.isArray(items) || items.length === 0) return [];

        const grouped = items.reduce((acc, item) => {
            const dept =
                item?.DepartmentName ||
                item?.departmentName ||
                item?.Department ||
                "General";

            if (!acc[dept]) {
                acc[dept] = [];
            }

            acc[dept].push(item);
            return acc;
        }, {});

        return Object.entries(grouped).map(([departmentName, deptItems]) => ({
            key: `${type}-${departmentName}`,
            departmentName,
            items: deptItems,
            heading: `${getHeadingBase(type)} - ${departmentName}`,
            type,
        }));
    };

    const printJobs = useMemo(() => {
        const jobs = [];
        jobs.push(...groupItemsByDepartment(orderAddItems, "ADD"));
        jobs.push(...groupItemsByDepartment(orderLessItems, "LESS"));
        return jobs;
    }, [orderAddItems, orderLessItems]);

    useEffect(() => {
        jobRefs.current = jobRefs.current.slice(0, printJobs.length);
    }, [printJobs.length]);

    useEffect(() => {
        if (printJobs.length === 0) {
            hasStartedRef.current = false;
            hasCompletedRef.current = false;
            setCurrentJobIndex(null);
            return;
        }

        if (!hasStartedRef.current) {
            hasStartedRef.current = true;
            hasCompletedRef.current = false;
            setCurrentJobIndex(0);
        }
    }, [printJobs]);

    const handlePrintJob = useReactToPrint({
        content: () =>
            currentJobIndex === null
                ? null
                : jobRefs.current[currentJobIndex] || null,
        documentTitle:
            currentJobIndex === null
                ? "KOT"
                : `${printJobs[currentJobIndex]?.heading || "KOT"}_${orderDetails?.orderNumber || orderDetails?.OrderNumber || "unknown"}`,
        pageStyle: "@page { size: auto; margin: 0mm; }",
        onAfterPrint: () => {
            setCurrentJobIndex((prev) => (prev === null ? prev : prev + 1));
        },
    });

    useEffect(() => {
        if (currentJobIndex === null) return;

        const currentJob = printJobs[currentJobIndex];

        if (!currentJob) {
            setCurrentJobIndex(null);
            if (!hasCompletedRef.current) {
                hasCompletedRef.current = true;
                if (typeof onComplete === "function") {
                    onComplete();
                }
            }
            return;
        }

        if (!jobRefs.current[currentJobIndex]) {
            return;
        }

        handlePrintJob();
    }, [currentJobIndex, printJobs, handlePrintJob, onComplete]);

    return (
        <div>
            {printJobs.map((job, index) => (
                <ReceiptTemplate
                    key={job.key}
                    ref={(el) => {
                        jobRefs.current[index] = el;
                    }}
                    order={orderDetails}
                    items={job.items}
                    heading={job.heading}
                />
            ))}
        </div>
    );
};

const KotPrint = (props) => {

    const { groupByDepartment = false, addHeading = "KOT - ADD", lessHeading = "KOT - LESS" } = props;

    if (groupByDepartment) {
        return <DepartmentKotPrint {...props} />;
    }

    return <StandardKotPrint {...props} addHeading={addHeading} lessHeading={lessHeading} />;
};

export default KotPrint;